import React, { useEffect, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { useAuth } from "../contexts/AuthContext";
import ToolCard from "../components/ToolCard";
import TabIcon, { resolveTabIcon } from "../components/TabIcon";
import AdminPage from "./AdminPage";
import { resolveCategories, loadReactions, setReaction, loadReads, markAnnouncementRead } from "../services/storageService";

// Home tem icone fixo de casa (nao e configuravel pelo admin).
const HOME_TAB = { id: "home", label: "Home", icon: "home" };

export default function PortalPage({ linksData, onLinksUpdate, announcementsData, onAnnouncementsUpdate, settingsData, onSettingsUpdate }) {
  const { role, account } = useAuth();
  const [activeTab, setActiveTab] = useState(HOME_TAB.id);
  const [showAdmin, setShowAdmin] = useState(false);

  const canSeeRestricted = role === "admin" || role === "lider";
  const isAdmin = role === "admin";
  const categories = resolveCategories(settingsData);

  const email = (account?.username || "").toLowerCase();
  const userName = account?.name || "";

  // Reações aos recados (👍/👎) e controle de "lidos" para o sino.
  const [reactions, setReactions] = useState({}); // { annId: { email: "up"|"down" } }
  const [readIds, setReadIds] = useState(() => new Set()); // recados que este usuário já leu

  useEffect(() => {
    loadReactions().then(d => setReactions(d.reactions || {})).catch(() => {});
    if (email) {
      loadReads()
        .then(d => setReadIds(new Set(d.reads?.[email]?.readIds || [])))
        .catch(() => {});
    }
  }, [email]);

  // Recados válidos (não expirados), base para o sino de notificações.
  const validAnnouncements = (announcementsData.announcements || [])
    .filter(a => !a.expiresAt || new Date(a.expiresAt) > new Date())
    .sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""));

  const unreadIds = new Set(validAnnouncements.filter(a => !readIds.has(a.id)).map(a => a.id));

  function handleReact(annId, reaction) {
    if (!email) return;
    const current = reactions[annId]?.[email];
    const currentType = typeof current === "string" ? current : current?.r; // compat formato antigo
    const next = currentType === reaction ? null : reaction; // clicar de novo remove
    // Atualização otimista da UI
    setReactions(prev => {
      const forAnn = { ...(prev[annId] || {}) };
      if (next) forAnn[email] = { r: next, n: userName };
      else delete forAnn[email];
      return { ...prev, [annId]: forAnn };
    });
    setReaction({ announcementId: annId, email, name: userName, reaction: next }).catch(() => {});
  }

  // Marca UM recado como lido (só quando o usuário clica nele) e vai pra Home.
  function handleSelectNotification(annId) {
    setActiveTab(HOME_TAB.id);
    setShowAdmin(false);
    if (!email || readIds.has(annId)) return;
    setReadIds(prev => new Set(prev).add(annId)); // otimista
    markAnnouncementRead({ email, announcementId: annId }).catch(() => {});
  }

  const publicTabs = [HOME_TAB, ...linksData.public];
  const restrictedTabs = canSeeRestricted ? linksData.restricted : [];

  const allTabs = [...publicTabs, ...restrictedTabs];
  const activeSection = allTabs.find(t => t.id === activeTab);
  const isHome = activeTab === HOME_TAB.id;

  function handleTabChange(id) {
    setActiveTab(id);
    setShowAdmin(false);
  }

  const pageTitle = showAdmin
    ? "Administração"
    : (activeSection?.label || "Portal");

  // Recados fixos ativos (nao expirados), usados na sidebar direita.
  const pinnedAnnouncements = (announcementsData.announcements || [])
    .filter(a => a.pinned && (!a.expiresAt || new Date(a.expiresAt) > new Date()))
    .sort((a, b) => (a.priority || 999) - (b.priority || 999))
    .slice(0, 5);

  const showPinned = !showAdmin && pinnedAnnouncements.length > 0;

  return (
    <div className={`app-layout${showPinned ? " app-layout-with-pinned" : ""}`}>
      <Sidebar
        publicTabs={publicTabs}
        restrictedTabs={restrictedTabs}
        activeTab={showAdmin ? null : activeTab}
        onTabChange={handleTabChange}
        account={account}
        role={role}
        isAdmin={isAdmin}
        showAdmin={showAdmin}
        setShowAdmin={setShowAdmin}
        logoUrl={settingsData?.logoUrl || ""}
      />

      <main className="main">
        <MainHeader
          title={pageTitle}
          unreadCount={unreadIds.size}
          notifications={validAnnouncements}
          unreadIds={unreadIds}
          onSelect={handleSelectNotification}
        />

        <div className="content">
          {showAdmin && isAdmin ? (
            <AdminPage
              linksData={linksData}
              onSaved={onLinksUpdate}
              announcementsData={announcementsData}
              onAnnouncementsSaved={onAnnouncementsUpdate}
              settingsData={settingsData}
              onSettingsSaved={onSettingsUpdate}
            />
          ) : isHome ? (
            <HomeSection
              announcements={announcementsData.announcements || []}
              categories={categories}
              reactions={reactions}
              userEmail={email}
              onReact={handleReact}
            />
          ) : (
            <RegularSection section={activeSection} isRestricted={restrictedTabs.some(t => t.id === activeTab)} categories={categories} />
          )}
        </div>
      </main>

      {showPinned && <PinnedSidebar announcements={pinnedAnnouncements} />}
    </div>
  );
}

function PinnedSidebar({ announcements }) {
  return (
    <aside className="pinned-sidebar">
      <div className="pinned-header">
        <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 16, height: 16 }}>
          <path d="M16 9V4h1c.55 0 1-.45 1-1s-.45-1-1-1H7c-.55 0-1 .45-1 1s.45 1 1 1h1v5c0 1.66-1.34 3-3 3v2h5.97v7l1 1 1-1v-7H19v-2c-1.66 0-3-1.34-3-3z"/>
        </svg>
        <h3>Recados fixos</h3>
        <span>{announcements.length} / 5</span>
      </div>
      {announcements.map(a => {
        const linkUrl = (a.linkUrl || "").trim();
        const text = a.text != null ? a.text : (a.type === "text" ? (a.content || "") : "");
        const Wrapper = linkUrl ? "a" : "div";
        const wrapperProps = linkUrl
          ? { href: linkUrl, target: "_blank", rel: "noopener noreferrer", className: "pin-card pin-card-link" }
          : { className: "pin-card" };
        return (
          <Wrapper key={a.id} {...wrapperProps}>
            {a.title && <div className="pin-title">{a.title}</div>}
            {text && <div className="pin-text">{text}</div>}
            <div className="pin-meta">
              {a.subtab && <span className="pin-pill">{a.subtab}</span>}
              <span>{formatRelativeDate(a.createdAt)}</span>
              {linkUrl && <span aria-hidden="true">↗</span>}
            </div>
          </Wrapper>
        );
      })}
    </aside>
  );
}

function formatRelativeDate(iso) {
  if (!iso) return "";
  const diffMs = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (days < 1) return "hoje";
  if (days === 1) return "ontem";
  if (days < 7) return `há ${days} dias`;
  if (days < 30) return `há ${Math.floor(days / 7)} sem.`;
  if (days < 365) return `há ${Math.floor(days / 30)} mês${Math.floor(days / 30) > 1 ? "es" : ""}`;
  return `há ${Math.floor(days / 365)} ano${Math.floor(days / 365) > 1 ? "s" : ""}`;
}

function Sidebar({ publicTabs, restrictedTabs, activeTab, onTabChange, account, role, isAdmin, showAdmin, setShowAdmin, logoUrl }) {
  const { instance } = useMsal();
  const initials = getInitials(account?.name || account?.username || "");

  return (
    <aside className="sidebar">
      <div className="sidebar-logo">
        {logoUrl ? (
          <img src={logoUrl} alt="Logo" className="logo-custom" />
        ) : (
          <>
            <div className="logo-icon">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M13 2.05v2.02c3.95.49 7 3.85 7 7.93 0 3.21-1.81 6-4.72 7.28L13 17v5l5-3-1.22-1.22C19.91 16.07 22 13.14 22 10c0-5.18-3.95-9.45-9-9.95zM11 2.05C5.95 2.55 2 6.82 2 12c0 3.14 2.09 6.07 5.22 7.78L6 21l5 3v-5l-2.28 2.28C6.81 20 5 17.21 5 14c0-4.08 3.05-7.44 7-7.93V2.05z"/></svg>
            </div>
            <div>
              <div className="logo-text">Portal Power</div>
              <div className="logo-sub">Tuning</div>
            </div>
          </>
        )}
      </div>

      <nav className="sidebar-nav">
        <div className="nav-section">
          <div className="nav-section-title">Geral</div>
          {publicTabs.map(t => (
            <NavItem
              key={t.id}
              label={t.label}
              icon={<TabIcon name={resolveTabIcon(t)} />}
              active={activeTab === t.id}
              onClick={() => onTabChange(t.id)}
            />
          ))}
        </div>

        {restrictedTabs.length > 0 && (
          <div className="nav-section">
            <div className="nav-section-title">Restrito</div>
            {restrictedTabs.map(t => (
              <NavItem
                key={t.id}
                label={t.label}
                icon={<TabIcon name={resolveTabIcon(t)} />}
                active={activeTab === t.id}
                onClick={() => onTabChange(t.id)}
                restricted
              />
            ))}
          </div>
        )}
      </nav>

      <div className="sidebar-spacer" />

      <div className="sidebar-footer">
        {isAdmin && (
          <button
            className={`sidebar-btn${showAdmin ? " sidebar-btn-active" : ""}`}
            onClick={() => setShowAdmin(s => !s)}
          >
            {showAdmin ? "← Voltar ao Portal" : "Administração"}
          </button>
        )}
        <div className="sidebar-user">
          <div className="avatar">{initials}</div>
          <div className="user-info">
            <div className="user-name">{account?.name || account?.username}</div>
            <div className="user-role">{roleLabel(role)}</div>
          </div>
        </div>
        <button
          className="sidebar-btn sidebar-btn-logout"
          onClick={() => {
            // Limpa o marcador de inicio de sessao para nao herdar 8h restantes
            // se o usuario logar de novo na mesma aba.
            try { sessionStorage.removeItem("ptp_login_at"); } catch {}
            instance.logoutPopup();
          }}
        >
          Sair
        </button>
      </div>
    </aside>
  );
}

function NavItem({ label, icon, active, onClick, restricted }) {
  return (
    <button
      className={`nav-item${active ? " active" : ""}${restricted ? " restricted" : ""}`}
      onClick={onClick}
    >
      <span className="nav-icon">{icon}</span>
      <span>{label}</span>
    </button>
  );
}

function MainHeader({ title, unreadCount = 0, notifications = [], unreadIds, onSelect }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div className="main-header">
      <div className="page-title">{title}</div>
      <div className="notif" ref={ref}>
        <button className="notif-bell" onClick={() => setOpen(o => !o)} aria-label="Notificações" title="Notificações">
          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
            <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/>
          </svg>
          {unreadCount > 0 && <span className="notif-badge">{unreadCount > 99 ? "99+" : unreadCount}</span>}
        </button>
        {open && (
          <div className="notif-panel">
            <div className="notif-panel-header">
              Recados{unreadCount > 0 ? ` — ${unreadCount} não lido${unreadCount > 1 ? "s" : ""}` : ""}
            </div>
            {notifications.length === 0 ? (
              <div className="notif-empty">Nenhum recado no momento.</div>
            ) : (
              notifications.map(a => (
                <button
                  key={a.id}
                  className={`notif-item${unreadIds && unreadIds.has(a.id) ? " notif-item-unread" : ""}`}
                  onClick={() => { setOpen(false); onSelect && onSelect(a.id); }}
                >
                  <div className="notif-item-title">{notifLabel(a)}</div>
                  <div className="notif-item-date">{formatRelativeDate(a.createdAt)}</div>
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function notifLabel(a) {
  if (a.title) return a.title;
  const text = a.text != null ? a.text : (a.type === "text" ? (a.content || "") : "");
  return text ? text.slice(0, 60) : "Recado";
}

// Pílulas disponíveis: primeiro as categorias configuradas que têm conteúdo,
// depois qualquer sub-aba órfã (categoria removida mas ainda usada em algum item),
// para não esconder conteúdo já cadastrado.
function orderedAvailableSubtabs(categories, items) {
  const present = [...new Set(items.map(i => i.subtab).filter(Boolean))];
  const inOrder = categories.filter(s => present.includes(s));
  const extras = present.filter(s => !categories.includes(s));
  return [...inOrder, ...extras];
}

function RegularSection({ section, isRestricted, categories = [] }) {
  const [activeSubtab, setActiveSubtab] = useState("");

  // Reseta a sub-aba sempre que muda de aba principal
  useEffect(() => { setActiveSubtab(""); }, [section?.id]);

  if (!section) return null;

  const allItems = section.items || [];
  // Só mostra as pílulas de sub-aba que realmente têm conteúdo,
  // pra não poluir abas que não usam esse recurso.
  const availableSubtabs = orderedAvailableSubtabs(categories, allItems);
  const hasUncategorized = allItems.some(item => !item.subtab);

  const visibleItems = activeSubtab
    ? allItems.filter(item => item.subtab === activeSubtab)
    : allItems;

  return (
    <>
      {isRestricted && (
        <div className="restricted-banner">
          <svg viewBox="0 0 24 24"><path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/></svg>
          <span>Acesso restrito — disponível apenas para líderes e administradores</span>
        </div>
      )}

      {availableSubtabs.length > 0 && (
        <div className="subtab-nav">
          <button
            className={`subtab-pill${activeSubtab === "" ? " active" : ""}`}
            onClick={() => setActiveSubtab("")}
          >
            Todos
          </button>
          {availableSubtabs.map(s => (
            <button
              key={s}
              className={`subtab-pill${activeSubtab === s ? " active" : ""}`}
              onClick={() => setActiveSubtab(s)}
            >
              {s}
            </button>
          ))}
          {hasUncategorized && (
            <button
              className={`subtab-pill${activeSubtab === "__none__" ? " active" : ""}`}
              onClick={() => setActiveSubtab("__none__")}
              title="Links ainda não categorizados"
            >
              Sem categoria
            </button>
          )}
        </div>
      )}

      <div className="cards-grid">
        {[...(activeSubtab === "__none__"
            ? allItems.filter(i => !i.subtab)
            : visibleItems)]
          .sort((a, b) => (a.title || "").localeCompare(b.title || "", "pt-BR", { sensitivity: "base" }))
          .map(item => (
            <ToolCard key={item.id} {...item} />
          ))}
        {allItems.length === 0 && (
          <p style={{ color: "var(--text3)", fontSize: 13, gridColumn: "1/-1" }}>
            Nenhuma ferramenta cadastrada nesta aba ainda.
          </p>
        )}
        {allItems.length > 0 && visibleItems.length === 0 && activeSubtab && activeSubtab !== "__none__" && (
          <p style={{ color: "var(--text3)", fontSize: 13, gridColumn: "1/-1" }}>
            Nenhuma ferramenta em <strong>{activeSubtab}</strong> nesta aba.
          </p>
        )}
      </div>
    </>
  );
}

function HomeSection({ announcements, categories = [], reactions = {}, userEmail = "", onReact }) {
  const now = new Date();
  const valid = [...announcements]
    .filter(a => !a.expiresAt || new Date(a.expiresAt) > now)
    .sort((a, b) => {
      // Prioridade crescente (1 primeiro). Sem prioridade vai pro final; empate desempata por data desc.
      const pa = a.priority && a.priority > 0 ? a.priority : 9999;
      const pb = b.priority && b.priority > 0 ? b.priority : 9999;
      if (pa !== pb) return pa - pb;
      return (b.createdAt || "").localeCompare(a.createdAt || "");
    });

  const [lightbox, setLightbox] = useState(null);
  const [activeSubtab, setActiveSubtab] = useState("");

  // Só mostra pílulas das sub-abas que realmente têm recados válidos
  const availableSubtabs = orderedAvailableSubtabs(categories, valid);
  const hasUncategorized = valid.some(a => !a.subtab);

  const filtered = activeSubtab === "__none__"
    ? valid.filter(a => !a.subtab)
    : activeSubtab
      ? valid.filter(a => a.subtab === activeSubtab)
      : valid;
  const latest = filtered.slice(0, 30);

  return (
    <div className="home-section">
      <div className="home-hero">
        <h1>Bem-Vindo à Power Tuning</h1>
        <p>Últimos recados do time de Pessoas &amp; Cultura</p>
      </div>

      {availableSubtabs.length > 0 && (
        <div className="subtab-nav">
          <button
            className={`subtab-pill${activeSubtab === "" ? " active" : ""}`}
            onClick={() => setActiveSubtab("")}
          >
            Todos
          </button>
          {availableSubtabs.map(s => (
            <button
              key={s}
              className={`subtab-pill${activeSubtab === s ? " active" : ""}`}
              onClick={() => setActiveSubtab(s)}
            >
              {s}
            </button>
          ))}
          {hasUncategorized && (
            <button
              className={`subtab-pill${activeSubtab === "__none__" ? " active" : ""}`}
              onClick={() => setActiveSubtab("__none__")}
              title="Recados ainda não categorizados"
            >
              Sem categoria
            </button>
          )}
        </div>
      )}

      {valid.length === 0 ? (
        <p style={{ color: "var(--text3)", fontSize: 14, textAlign: "center", padding: "32px 0" }}>
          Nenhum recado publicado ainda.
        </p>
      ) : latest.length === 0 ? (
        <p style={{ color: "var(--text3)", fontSize: 14, textAlign: "center", padding: "32px 0" }}>
          Nenhum recado em <strong>{activeSubtab === "__none__" ? "Sem categoria" : activeSubtab}</strong>.
        </p>
      ) : (
        <div className="announcements-grid">
          {latest.map(a => (
            <AnnouncementCard
              key={a.id}
              announcement={a}
              onImageClick={setLightbox}
              reactions={reactions[a.id] || {}}
              userEmail={userEmail}
              onReact={onReact}
            />
          ))}
        </div>
      )}

      {lightbox && <Lightbox src={lightbox} onClose={() => setLightbox(null)} />}
    </div>
  );
}

function AnnouncementCard({ announcement, onImageClick, reactions = {}, userEmail = "", onReact }) {
  const { title } = announcement;
  // Novo formato: text + imageUrl. Compat com formato antigo: type + content.
  const text  = announcement.text  != null ? announcement.text
              : (announcement.type === "text"  ? (announcement.content || "") : "");
  const image = announcement.imageUrl != null ? announcement.imageUrl
              : (announcement.type === "image" ? (announcement.content || "") : "");
  const linkUrl = (announcement.linkUrl || "").trim();

  const entries = Object.entries(reactions); // [ [email, valor], ... ]
  const upNames = entries.filter(([, v]) => reactType(v) === "up").map(([mail, v]) => reactName(mail, v));
  const downNames = entries.filter(([, v]) => reactType(v) === "down").map(([mail, v]) => reactName(mail, v));
  const upCount = upNames.length;
  const downCount = downNames.length;
  const mine = userEmail ? reactType(reactions[userEmail]) : null;

  return (
    <div className={`announcement-card${linkUrl ? " has-link" : ""}`}>
      {image && (
        <img
          src={image}
          alt={title || "Recado"}
          className="announcement-image"
          onClick={() => onImageClick && onImageClick(image)}
          title="Clique para ampliar"
        />
      )}
      {title && <div className="announcement-title">{title}</div>}
      {text && <div className="announcement-text">{text}</div>}
      {linkUrl && (
        <a
          href={linkUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="announcement-link"
        >
          Abrir link
          <span aria-hidden="true" style={{ marginLeft: 6 }}>↗</span>
        </a>
      )}
      {onReact && (
        <div className="reaction-bar">
          <button
            className={`reaction-btn${mine === "up" ? " reaction-up-active" : ""}`}
            onClick={() => onReact(announcement.id, "up")}
            aria-label="Curtir"
            aria-pressed={mine === "up"}
          >
            <span aria-hidden="true">👍</span>
            {upCount > 0 && <span className="reaction-count">{upCount}</span>}
            {upCount > 0 && <ReactionTip names={upNames} />}
          </button>
          <button
            className={`reaction-btn${mine === "down" ? " reaction-down-active" : ""}`}
            onClick={() => onReact(announcement.id, "down")}
            aria-label="Não curtir"
            aria-pressed={mine === "down"}
          >
            <span aria-hidden="true">👎</span>
            {downCount > 0 && <span className="reaction-count">{downCount}</span>}
            {downCount > 0 && <ReactionTip names={downNames} />}
          </button>
        </div>
      )}
    </div>
  );
}

// Tooltip com a lista de quem reagiu (aparece ao passar o mouse na reação).
function ReactionTip({ names }) {
  return (
    <span className="reaction-tip" role="tooltip">
      {names.map((n, i) => (
        <span key={i} className="reaction-tip-name">{n}</span>
      ))}
    </span>
  );
}

// Normaliza o valor da reação (compat: string antiga "up"/"down" ou objeto { r, n }).
function reactType(v) {
  if (typeof v === "string") return v;
  return v && typeof v === "object" ? v.r : null;
}

// Nome de quem reagiu: usa o nome salvo; senão deriva do e-mail (nome.sobrenome@...).
function reactName(email, v) {
  const saved = v && typeof v === "object" ? v.n : "";
  if (saved) return saved;
  return prettyNameFromEmail(email);
}

function prettyNameFromEmail(email) {
  const local = (email || "").split("@")[0];
  if (!local) return email || "Alguém";
  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map(p => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");
}

function Lightbox({ src, onClose }) {
  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose(); }
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return (
    <div className="lightbox-overlay" onClick={onClose}>
      <button className="lightbox-close" onClick={onClose} aria-label="Fechar">×</button>
      <img src={src} alt="" className="lightbox-image" onClick={e => e.stopPropagation()} />
    </div>
  );
}

function roleLabel(role) {
  if (role === "admin") return "Administrador";
  if (role === "lider") return "Líder";
  return "Usuário";
}

function getInitials(nameOrEmail) {
  if (!nameOrEmail) return "?";
  const clean = nameOrEmail.split("@")[0];
  const parts = clean.split(/[\s.\-_]+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
